// ABOUTME: Renders the catalog product card content: the model name, its start actions and its image.
// ABOUTME: Fills the model-selection card's OverrideContent slot so models look like the category cards.

import type { IProduct } from '@coolshop/cool-model-selection';
import { CameraAltOutlinedIcon, Img, Stack, Typography } from '@ds/coolshop';
import { ReactNode, useState } from 'react';

import { getProductImageUrl } from './getProductImageUrl';
import { ProductCardStartActions } from './ProductCardStartActions';

export type ProductCardContentProps = IProduct & {
    /** The description with the search term highlighted, supplied by the card. */
    modelNameHighlighted?: ReactNode;
    showFavorite?: boolean;
};

/**
 * Content of the model-selection product card. It replaces the card's whole content, so it renders
 * the start actions itself. The image is square and contained, and falls back to a placeholder of
 * the same size when the model has none or it fails to load, so the grid stays uniform.
 */
export function ProductCardContent({
    modelNameHighlighted,
    showFavorite,
    ...product
}: ProductCardContentProps) {
    const [imageFailed, setImageFailed] = useState(false);
    const imageUrl = getProductImageUrl(product);

    return (
        <Stack gap={1} height="100%" justifyContent="space-between">
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1}>
                <Typography variant="subtitle1">
                    {modelNameHighlighted ?? product.description}
                </Typography>
                <ProductCardStartActions {...product} showFavorite={showFavorite} />
            </Stack>
            {imageUrl && !imageFailed ? (
                <Img
                    src={imageUrl}
                    alt={product.description}
                    objectFit="contain"
                    aspectRatio={1}
                    width="100%"
                    borderRadius={1}
                    onError={() => setImageFailed(true)}
                />
            ) : (
                <Stack
                    alignItems="center"
                    justifyContent="center"
                    aspectRatio={1}
                    width="100%"
                    color="text.disabled"
                >
                    <CameraAltOutlinedIcon />
                </Stack>
            )}
        </Stack>
    );
}
